import { Transformer } from '@lexical/markdown';
import {
  $applyNodeReplacement,
  $createTextNode,
  DecoratorNode,
  LexicalNode,
  TextNode,
} from 'lexical';
import { lazy, ReactNode, Suspense } from 'react';

import { parseInternalLinkHref } from './internalLinks';

// The renderer pulls in the route table (and therefore the chat page, which
// renders the editor that registers this node). Loading it lazily keeps that
// graph out of the core editor's static imports, so registering this node /
// transformer can never form an import cycle. The navigation happens only when
// a link actually renders, long after module-init.
const InternalLink = lazy(() =>
  import('./InternalLink').then((m) => ({ default: m.InternalLink }))
);

/**
 * A luminary:// internal link in a chat message, e.g.
 * [Jones SLAT](luminary://record/entity_01HZX...). Rendered as an InternalLink
 * that navigates with the client-side router. See internalLinks.ts for the
 * target vocabulary.
 */
export class InternalLinkNode extends DecoratorNode<ReactNode> {
  __label: string;
  __href: string;

  static override getType(): string {
    return 'chat-internal-link';
  }

  static override clone(node: InternalLinkNode): InternalLinkNode {
    return $applyNodeReplacement(
      new InternalLinkNode(node.__label, node.__href, node.__key)
    );
  }

  constructor(label: string, href: string, key?: string) {
    super(key);
    this.__label = label;
    this.__href = href;
  }

  override createDOM(): HTMLElement {
    const span = document.createElement('span');
    span.setAttribute('data-testid', 'InternalLinkNode');
    return span;
  }

  override updateDOM(): false {
    return false;
  }

  override exportJSON(): {
    label: string;
    href: string;
    type: string;
    version: number;
  } {
    return {
      label: this.__label,
      href: this.__href,
      type: InternalLinkNode.getType(),
      version: 1,
    };
  }

  static override importJSON(serializedNode: {
    label: string;
    href: string;
    type: string;
    version: number;
  }): InternalLinkNode {
    return $createInternalLinkNode(serializedNode.label, serializedNode.href);
  }

  override decorate(): ReactNode {
    return (
      <Suspense fallback={<span>{this.__label}</span>}>
        <InternalLink
          key={this.__key}
          label={this.__label}
          href={this.__href}
        />
      </Suspense>
    );
  }

  getLabel(): string {
    return this.__label;
  }

  getHref(): string {
    return this.__href;
  }
}

export function $createInternalLinkNode(
  label: string,
  href: string
): InternalLinkNode {
  return new InternalLinkNode(label, href);
}

// Matches a [label](luminary://target) markdown link. The optional
// `(?:\s+"…")?` matches a markdown link title (`[label](luminary://… "title")`).
// Agents emit these even though the backend's link regex doesn't recognize or
// strip them, and without it the link falls through to the built-in LINK
// transformer and becomes a dead luminary:// anchor that opens a blank browser
// tab. The title is consumed but discarded — it is not part of the link target.
const INTERNAL_LINK_REGEXP =
  /\[([^[\]]+)\]\((luminary:\/\/[^()\s]*)(?:\s+"[^"]*")?\)/;

// The text-match transformer's `regExp` fires on the trigger char, so it must
// be anchored to the end of the text; `importRegExp` (paste/import) must not be.
const INTERNAL_LINK_END_REGEXP = new RegExp(`${INTERNAL_LINK_REGEXP.source}$`);

/**
 * Markdown transformer for luminary:// internal links emitted by the chat
 * agent. It is registered ahead of the built-in LINK transformer (see
 * `transformers.ts`) so internal links become InternalLinkNodes instead of
 * LinkNode anchors — a LinkNode on the luminary:// scheme renders as a dead
 * `about:blank` anchor that opens a blank browser tab.
 */
export const internalLinkTransformer: Transformer = {
  dependencies: [InternalLinkNode],
  export: (node: LexicalNode) => {
    if (node instanceof InternalLinkNode) {
      return `[${node.getLabel()}](${node.getHref()})`;
    }
    return null;
  },
  importRegExp: INTERNAL_LINK_REGEXP,
  regExp: INTERNAL_LINK_END_REGEXP,
  replace: (textNode: TextNode, match: RegExpMatchArray) => {
    const [, label, href] = match;
    if (!label || !href) {
      return;
    }

    // The backend sanitizes invalid targets to plain text before persisting,
    // but guard anyway: an unparseable target renders its label as plain
    // text instead of a dead link.
    if (!parseInternalLinkHref(href)) {
      textNode.replace($createTextNode(label));
      return;
    }

    textNode.replace($createInternalLinkNode(label, href));
  },
  trigger: ')',
  type: 'text-match',
};
